import { Component, OnInit } from '@angular/core';
import { FeatureFlag, FeatureFlagGroup } from '../../../../../Models/FeatureFlags';
import { TenantService } from "../../../../../Services";
import { AvaModule } from '../../Models';
import { TranslationService } from '../../Services/translation.service';
import { PermissionService } from '../../../../../Services/permission.service';
import { ToastMessageType, ToastService } from '../../../../../Common/toast';
import { filterFlagsBySearch, resolveDisplayNames, sortGroupsByDisplayName } from './feature-flag-search.utils';
import { getFeatureFlagDescription } from './feature-flag-descriptions';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';

interface ModuleItem {
    id: string,
    text: string
}

@Component({
    selector: 'aw-feature-flags',
    templateUrl: './feature-flag.component.html',
    styleUrls: ['./feature-flag.component.less'],
    imports: [MatButton, MatFormField, MatInput, MatLabel, FormsModule, MatIcon, MatSelect, MatOption, MatCheckbox, MatTooltip, AsyncPipe, JsonPipe]
})
export class FeatureFlagsComponent implements OnInit {
    public error: any[];
    public featureFlags: FeatureFlag[];
    public modules: ModuleItem[];
    public loading: boolean;
    public saving: boolean;
    public hasPermission: boolean;
    public groups: FeatureFlagGroup[];
    public selectedGroup: FeatureFlagGroup;
    public groupToggle: boolean;
    public groupToggleCanSave: boolean;
    public flagsFilter: string = '';
    public visibleFlags: FeatureFlag[] = [];
    private flagDisplayNames: Map<string, string>;
    private unmodifiedFeatureFlags: FeatureFlag[];
    private modulesMap: { [key: string]: string };
    public newFeatureFlagName: string;
   
    constructor (
        public translationService: TranslationService,
        private readonly tenantService: TenantService,
        private readonly permissionService: PermissionService,
        private readonly toast: ToastService,
    ) {
        this.selectedGroup = { Name: 'All', LabelAlias: 'feature:All' };
        this.loading = true;
        this.saving = false;
        this.error = [];
        this.featureFlags = [];
        this.unmodifiedFeatureFlags = [];
        this.groups = [];
        this.groupToggle = false;
        this.groupToggleCanSave = false;
        this.modules = [];
        this.modulesMap = {};
        this.flagDisplayNames = new Map<string, string>();
        this.hasPermission = this.permissionService.hasPermission("Feature Flags");
        this.newFeatureFlagName = '';        
    }

    ngOnInit(): void {
        this.getFeatureFlags();
    }

    public async clearMobileCache(): Promise<void> {
        await this.tenantService.clearMobileCache().toPromise();
    }

    public groupChanged(): void {
        this.groupToggle = this.filteredFlags().every(featureFlag => featureFlag.Enabled);
        this.refreshVisibleFlags();
    }

    public groupToggleChanged(): void {
        this.groupToggleCanSave = true;
        this.filteredFlags().forEach(featureFlag => featureFlag.Enabled = this.groupToggle);
    }

    public resetGroupToggle(): void {
        this.groupToggleCanSave = false;
        this.featureFlags = this.cloneFlags(this.unmodifiedFeatureFlags);
        this.groupChanged();
    }

    public async saveGroupToggle(): Promise<void> {
        const self = this;

        if (!this.saving) {
            this.error = [];
            try {
                this.saving = true;
                for(let featureFlag of this.filteredFlags()) {
                    await this.updateFeatureFlag(featureFlag);
                }
                this.groupToggleCanSave = false;
                this.saving = false;
                this.toast.sendMessage(
                    'Success',
                    ` Feature flags updated for group ${this.selectedGroup.Name}`,
                    ToastMessageType.success);
            }
            catch (e) {
                self.saving = false;
                self.error.push(e);
            }
        }
    }

    public filteredFlags(): FeatureFlag[] {
        if (this.selectedGroup.Name === 'All') {
            return this.featureFlags;
        } else {
            return this.featureFlags.filter(flag => flag.FeatureGroups.some(group => group.Name === this.selectedGroup.Name));
        }
    }

    public refreshVisibleFlags(): void {
        this.visibleFlags = filterFlagsBySearch(this.filteredFlags(), this.flagsFilter, this.flagDisplayNames);
    }

    public hasModification(featureFlag: FeatureFlag): boolean {
        const unmodified = this.unmodifiedFeatureFlags.find(flag => flag.Name == featureFlag.Name);
        if (!unmodified)
            return true;
        if (unmodified.Enabled !== featureFlag.Enabled)
            return true;
        if (unmodified.ModulesEnabledFor.length !== featureFlag.ModulesEnabledFor.length)
            return true;
        for (let i = 0; i < unmodified.ModulesEnabledFor.length; i++)
            if (unmodified.ModulesEnabledFor[i] !== featureFlag.ModulesEnabledFor[i])
                return true;

        return false;
    }

    public getDescription(featureFlag: FeatureFlag): string | undefined {
        return getFeatureFlagDescription(featureFlag.Name);
    }

    public hasAccess(): boolean {
        return this.hasPermission;
    }

    public async saveFlag(featureFlag: FeatureFlag) {
        const self = this;

        if (!this.saving) {
            this.error = [];
            try {
                this.saving = true;
                await this.updateFeatureFlag(featureFlag);
                this.saving = false;
                this.toast.sendMessage(
                    'Success',
                    `${featureFlag.Name} feature flag updated`,
                    ToastMessageType.success);
            }
            catch (e) {
                self.saving = false;
                self.error.push(e);
            }
        }
    }

    public async addFlag() {
        const self = this;

        if (this.newFeatureFlagName) {
            const newFeatureFlag: FeatureFlag = {
                AdditionalConfig: null,
                LabelAlias: `feature:${this.newFeatureFlagName}`,
                Enabled: true,
                ModulesEnabledFor: null,
                Name: this.newFeatureFlagName,
                FeatureGroups: null,
                Scope: null,
                GlobalFlag: true
            };
            try {
                this.saving = true;
                await this.tenantService.updateFeatureFlag(this.newFeatureFlagName, newFeatureFlag).toPromise();
                this.saving = false;
                this.toast.sendMessage(
                    'Success',
                    `${this.newFeatureFlagName} feature flag added`,
                    ToastMessageType.success);
                this.newFeatureFlagName = '';
                this.getFeatureFlags();
            }
            catch (e) {
                self.saving = false;
                self.error.push(e);
            }
        }
    }

    private async updateFeatureFlag(featureFlag: FeatureFlag): Promise<void> {
        await this.tenantService.updateFeatureFlag(featureFlag.Name, featureFlag).toPromise();
        this.unmodifiedFeatureFlags.find(flag => flag.Name === featureFlag.Name).Enabled = featureFlag.Enabled;
        this.unmodifiedFeatureFlags.find(flag => flag.Name === featureFlag.Name).ModulesEnabledFor = featureFlag.ModulesEnabledFor;    
    }

    public resetFlag(featureFlag: FeatureFlag): void {
        this.error = [];
        featureFlag.Enabled = this.unmodifiedFeatureFlags.find(flag => flag.Name === featureFlag.Name).Enabled;
        featureFlag.ModulesEnabledFor = this.unmodifiedFeatureFlags.find(flag => flag.Name === featureFlag.Name).ModulesEnabledFor;
    }
        
    private async getFeatureFlags(): Promise<void> {
        const self = this;
        this.error = [];
        this.modulesMap = {};

        async function transformModules(module: AvaModule) {
            try {
                const name = await self.translationService.getTranslation(module.Name, "Text").toPromise();
                self.modulesMap[module.UniqueId] = name;
                return { id: module.UniqueId, text: `${name} (${module.UniqueId})` };
            }
            catch (e) {
                self.error.push(e);
                return { id: module.UniqueId, text: module.UniqueId };
            }
        }

        const modules = await this.getAllModules();
        this.modules = await Promise.all(modules.map(transformModules));

        try {
            this.populateFeatureFlags(await this.tenantService.getFeatureFlags().toPromise());
            this.flagDisplayNames = await resolveDisplayNames(this.featureFlags, this.translationService);
            await this.sortGroupsByDisplayName();
            this.refreshVisibleFlags();
            this.loading = false;
        }
        catch (e) {
            self.error.push(e);
        }
    }

    private async getAllModules(): Promise<AvaModule[]> {
        const self = this;
        try {
            return await this.tenantService.getAllTenantModules(this.tenantService.selectedTenant.Value).toPromise();
        }
        catch (e) {
            self.error.push(e);
            return [];
        }
    }    

    private populateFeatureFlags(featureFlags: FeatureFlag[]) {
        this.featureFlags = featureFlags.sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        this.unmodifiedFeatureFlags = this.cloneFlags(this.featureFlags);
        this.populateGroups();
    }

    private populateGroups(): void {
        let groups: FeatureFlagGroup[] = [{ Name: 'All', LabelAlias: 'feature:All' }];

        for (let featureFlag of this.featureFlags) {
            for (let featureFlagGroup of featureFlag.FeatureGroups) {
                if (!groups.some(group => group.Name === featureFlagGroup.Name)) {
                    groups.push(featureFlagGroup);
                }
            }            
        }
        if (groups.length > 1) {
            this.groups = groups;
            this.selectedGroup = this.groups[0];
        }
    }

    // Keeps "All" first and lists the other groups alphabetically by their displayed label.
    private async sortGroupsByDisplayName(): Promise<void> {
        if (this.groups.length < 2) {
            return;
        }
        const [all, ...rest] = this.groups;
        const groupDisplayNames = await resolveDisplayNames(rest, this.translationService);
        this.groups = [all, ...sortGroupsByDisplayName(rest, groupDisplayNames)];
    }

    private cloneFlags(featureFlags: FeatureFlag[]): FeatureFlag[] {
        let clonedFlags: FeatureFlag[] = [];
        for (let featureFlag of featureFlags) {
            const clone = this.cloneFlag(featureFlag);
            clonedFlags.push(clone);
        }
        return clonedFlags;
    }

    private cloneFlag(featureFlag: FeatureFlag): FeatureFlag {
        const clone: FeatureFlag = {
            AdditionalConfig: featureFlag.AdditionalConfig,
            LabelAlias: featureFlag.LabelAlias,
            Scope: featureFlag.Scope,
            Enabled: featureFlag.Enabled,
            FeatureGroups: featureFlag.FeatureGroups,
            GlobalFlag: featureFlag.GlobalFlag,
            ModulesEnabledFor: featureFlag.ModulesEnabledFor,
            Name: featureFlag.Name
        };
        return clone;
    }
}